import { useState, useCallback } from 'react';
import { Stack } from '@mui/material';

import { HomeIcon, UnfoldMoreOutlinedIcon, AccountTreeIcon, FolderIcon } from 'app-zephyr-icons';
import { cutString } from 'app-zephyr-utils';
import { darkThemeOptions as theme } from 'app-zephyr-styles/themes';

import { BreadcrumbType } from './interfaces';
import { useStyles } from './styles';

const BreadCrumbsIcons = {
  [BreadcrumbType.ORGANIZATION]: HomeIcon,
  [BreadcrumbType.PROJECT]: FolderIcon,
  [BreadcrumbType.APPLICATION]: AccountTreeIcon,
};

const EmptyHolders = {
  [BreadcrumbType.ORGANIZATION]: 'Organization',
  [BreadcrumbType.PROJECT]: 'Project',
  [BreadcrumbType.APPLICATION]: 'Application',
};

// todo: get colors from theme
const BreadcrumbTypeColors = {
  [BreadcrumbType.ORGANIZATION]: {
    active: {
      stroke: theme.palette.brand.purple[300],
      fill: theme.palette.brand.purple[800],
      color: '#C8ACFF',
      border: theme.palette.brand.purple[800],
      bg: theme.palette.brand.purple[950],
    },
    previous: {
      color: theme.palette.brand.gray[200],
      border: theme.palette.brand.purple[800],
      bg: theme.palette.brand.purple[950],
      stroke: theme.palette.brand.purple[600],
      fill: theme.palette.brand.purple[600],
    },
  },
  [BreadcrumbType.PROJECT]: {
    active: {
      stroke: theme.palette.brand.turquoise[300],
      fill: theme.palette.brand.turquoise[600],
      color: theme.palette.brand.turquoise[25],
      border: theme.palette.brand.turquoise[700],
      bg: theme.palette.brand.turquoise[900],
    },
    previous: {
      color: theme.palette.brand.gray[200],
      border: theme.palette.brand.turquoise[700],
      bg: theme.palette.brand.turquoise[900],
      stroke: theme.palette.brand.turquoise[600],
      fill: theme.palette.brand.turquoise[600],
    },
  },
  [BreadcrumbType.APPLICATION]: {
    active: {
      stroke: theme.palette.brand.orange[300],
      fill: theme.palette.brand.orange[400],
      color: theme.palette.brand.orange[25],
      border: theme.palette.brand.orange[800],
      bg: theme.palette.brand.orange[950],
    },
    previous: {
      color: theme.palette.brand.gray[200],
      border: theme.palette.brand.orange[800],
      bg: theme.palette.brand.orange[950],
      stroke: theme.palette.brand.orange[700],
      fill: theme.palette.brand.orange[700],
    },
  },
};

export function BreadcrumbItem({
  value,
  isActive,
  isPrevious,
  type,
  counter,
  onClicked,
  navigateClick,
}: {
  onClicked: () => void;
  value: {
    organization?: string;
    project?: string;
    application?: string;
  };
  counter?: {
    organization?: string;
    project?: string;
    application?: string;
  };
  isActive: {
    organization?: boolean;
    project?: boolean;
    application?: boolean;
  };
  isPrevious: {
    organization?: boolean;
    project?: boolean;
    application?: boolean;
  };
  type: BreadcrumbType;
  navigateClick?: () => void;
}) {
  const { classes, cx, theme } = useStyles();

  const [isHovered, setIsHovered] = useState(false);
  const IconComponent = BreadCrumbsIcons[type];

  const getIconColor = useCallback(() => {
    if (isHovered && !isActive[type] && !isPrevious[type]) {
      return theme.palette.tx.white;
    }

    if (isActive[type]) {
      return BreadcrumbTypeColors[type].active.stroke;
    }

    if (isPrevious[type]) {
      return BreadcrumbTypeColors[type].previous.stroke;
    }

    return undefined;
  }, [isHovered, isActive, isPrevious, type]);

  return (
    <Stack
      data-e2e={`breadcrumb-${type}`}
      className={classes.itemBox}
      display={'flex'}
      flexDirection={'row'}
      justifyContent={'space-between'}
      alignItems={'center'}
      maxWidth={'250px'}
      onMouseEnter={() => {
        setIsHovered(true);
      }}
      onMouseLeave={() => {
        setIsHovered(false);
      }}
      onClick={onClicked}
    >
      <>
        <IconComponent
          style={{ transition: 'color 0.3s' }}
          color={getIconColor()}
          fill={
            (isActive[type] ? BreadcrumbTypeColors[type].active.fill : undefined) ??
            (isPrevious[type] ? BreadcrumbTypeColors[type].previous.fill : undefined)
          }
        />
        <span
          data-e2e={`breadcrumb-${type}-name`}
          data-full-name={value[type]}
          style={{
            color:
              (isActive[type] ? BreadcrumbTypeColors[type].active.color : undefined) ??
              (isPrevious[type] ? BreadcrumbTypeColors[type].previous.color : undefined),
          }}
          className={cx([isHovered && !isActive[type] && 'hovered'])}
          onClick={(event) => {
            if (!isPrevious[type] || !navigateClick) return;
            event.stopPropagation();
            navigateClick();
          }}
        >
          {cutString(15, value[type]) ?? EmptyHolders[type as keyof typeof EmptyHolders]}
        </span>
      </>
      {!!counter?.[type] && (
        <Stack
          data-e2e={`breadcrumb-${type}-counter`}
          className={cx([classes.itemCounter, isHovered && !isActive[type] && 'hovered'])}
          sx={{
            color:
              isActive[type] || isPrevious[type]
                ? `${BreadcrumbTypeColors[type].active.stroke ?? ''} !important`
                : undefined,
            borderColor:
              isActive[type] || isPrevious[type]
                ? `${BreadcrumbTypeColors[type].active.border ?? ''} !important`
                : undefined,
            backgroundColor:
              isActive[type] || isPrevious[type]
                ? `${BreadcrumbTypeColors[type].active.bg ?? ''} !important`
                : undefined,
          }}
        >
          {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */}
          {(isActive[type] || isPrevious[type]) && '+ '}
          {counter[type]}
        </Stack>
      )}
      <UnfoldMoreOutlinedIcon
        color={isHovered || isActive[type] || isPrevious[type] ? theme.palette.tx.white : undefined}
        style={{ transition: 'color 0.3s' }}
      />
    </Stack>
  );
}
